import { useEffect, useState } from "react";
import PersonIcon from "@mui/icons-material/Person";
import axiosClient from "@service/axiosClient";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { getUserInitials } from "@utils/general";
import PTOBankBalanceDialog from "../pto_dash/PTOBankBalanceDialog";
import UserHoursPageNew from "@view/user/base_user/UserHoursPageNew";
import { StatementSummaryNew } from "../payroll_components/user/StatementSummaryNew";
import UploadPhotoButton from "./UploadPhotoButton";
import "./UserProfileCard.css";

const UserProfileCardNew = ({ prop, stub, isAuthorized = true }) => {
  const { t } = useTranslation();
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [showPTOBalanceDialog, setShowPTOBalanceDialog] = useState(false);
  const [censored, setCensored] = useState(true);
  const [userData, setUserData] = useState({
    fullName: t("userProfile.noName"),
    plantName: t("userProfile.noPlant"),
    location: t("userProfile.noLocation"),
    contractorId: t("userProfile.noContractorId"),
    userInitials: "",
  });

  // This useEffect updates userData when prop changes
  useEffect(() => {
    if (prop && prop.worker) {
      setUserData({
        fullName: prop.full_name || t("userProfile.noName"),
        plantName: prop.worker.plant_name || t("userProfile.noPlant"),
        location: prop.worker.location || t("userProfile.noLocation"),
        contractorId:
          prop.worker.contractor_id || t("userProfile.noContractorId"),
        userInitials: getUserInitials(prop.full_name || ""),
      });
    }
  }, [prop, t]);

  const handleUploadSuccess = (newImageUrl) => {
    setProfileImageUrl(newImageUrl);
  };

  // Fetch profile image separately when user ID changes
  useEffect(() => {
    if (prop.id) {
      axiosClient
        .get("/file/profile-image", {
          params: { user_id: prop.id },
        })
        .then((response) => {
          setProfileImageUrl(response.data.user_image_url);
        })
        .catch((error) => {
          console.error("Failed to fetch profile image:", error);
        });
    }
  }, [prop.id]);

  const handleOpenPTOBalance = () => {
    setShowPTOBalanceDialog(true);
  };

  const handleClosePTOBalance = () => {
    setShowPTOBalanceDialog(false);
  };

  const onToggleCensor = () => {
    setCensored(!censored);
  };

  return (
    <>
      <div className=" tw-ml-3 tw-bg-white tw-rounded-lg tw-shadow tw-mr-6 tw-mt-2">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-bg-blue-50 tw-p-4 tw-rounded-md">
          {/* Image and User Info - Always horizontal */}
          <div className="tw-flex tw-flex-row tw-gap-4 tw-items-start tw-flex-1">
            <div className="tw-flex-shrink-0 tw-ml-4 profile-down-new">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="profile-image-new"
                />
              ) : (
                <PersonIcon
                  style={{
                    fontSize: "5.3em",
                    borderRadius: "50%",
                    border: "0.02em solid black",
                    marginBottom: "3px",
                    padding: "0.1em",
                    background: "white",
                  }}
                />
              )}
              {isAuthorized && (
                <UploadPhotoButton
                  onUploadSuccess={handleUploadSuccess}
                  userId={prop.id}
                />
              )}
            </div>
            <div className="tw-flex-1 tw-ml-2 tw-text-sm tw-leading-normal">
              <h3>{userData.fullName}</h3>
              <p>
                {t("userProfile.plant")}: {userData.plantName}
              </p>
              <p>
                {t("userProfile.location")}: {userData.location}
              </p>
              <p>
                {t("userProfile.contractorId")}: {userData.contractorId}
              </p>
            </div>
          </div>
          {/* Statement Summary - Goes below on mobile, right side on desktop */}
          <div>
            <Box
              sx={{ display: "flex", justifyContent: "end", mb: 0 }}
              className="tw-flex tw-flex-col tw-gap-2 tw-items-end"
            >
              <IconButton
                onClick={onToggleCensor}
                // size="small"
                sx={{ height: "15px", padding: "4px", fontSize: "0.75rem" }}
              >
                {censored ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}{" "}
                {t("profile.show")}
              </IconButton>
              <StatementSummaryNew stub={stub} isCensored={censored} />
            </Box>
          </div>
        </div>

        {/* PTO Bank Balance Dialog */}
        {showPTOBalanceDialog && prop.worker && (
          <PTOBankBalanceDialog
            workerId={prop.worker.id}
            contractorName={userData.fullName}
            contractorId={userData.contractorId}
            isOpen={showPTOBalanceDialog}
            onClose={handleClosePTOBalance}
            isAdminView={isAuthorized}
          />
        )}
      </div>

      {/* User Hours Page Component */}
      {prop.worker && prop.worker.id && (
        <UserHoursPageNew
          workerId={prop.worker.id}
          onOpenPTOBalance={handleOpenPTOBalance}
        />
      )}
    </>
  );
};

export default UserProfileCardNew;
